import type { RecoveryRecord, RecoveryStore } from "../storage/recovery";
import type { RecoveryLocation } from "./indexeddb-recovery";
import { copySceneValue } from "../native/history";

interface MediaHead {
  format: "creator-native-recovery-media";
  version: 1;
  revision: string;
  record: RecoveryRecord;
  media: { asset: number; key: string }[];
}

/** Native drafts write small scene metadata on each edit. Original media is
 * stored once per value. Head, new media and removal of obsolete media commit
 * in one transaction, so failed writes leave the previous draft recoverable. */
export class NativeRecoveryStore implements RecoveryStore {
  private media = new Map<string, string>();
  private initialized = false;
  private revision: string | undefined;
  constructor(
    private readonly factory: IDBFactory | undefined,
    private readonly location: RecoveryLocation,
  ) {}

  private open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (!this.factory) return reject(new Error("IndexedDB недоступен."));
      const request = this.factory.open(this.location.database, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(this.location.store))
          request.result.createObjectStore(this.location.store);
      };
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        request.result.onversionchange = () => request.result.close();
        resolve(request.result);
      };
    });
  }
  private key(id: string): IDBValidKey {
    return [this.location.key, "media", id];
  }

  async read(): Promise<unknown> {
    if (!this.factory) return null;
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.location.store, "readonly"),
        store = tx.objectStore(this.location.store);
      const request = store.get(this.location.key);
      let result: unknown = null,
        error: unknown;
      const media = new Map<string, string>();
      let revision: string | undefined;
      request.onsuccess = () => {
        const head = request.result as MediaHead | undefined;
        if (head?.format !== "creator-native-recovery-media") {
          result = head ?? null;
          return;
        }
        try {
          if (head.version !== 1 || !Array.isArray(head.media))
            throw new Error("Неподдерживаемый черновик.");
          result = head.record;
          revision = head.revision;
          for (const ref of head.media) {
            const asset = head.record.projectData.document.assets[ref.asset];
            if (!asset || typeof ref.key !== "string")
              throw new Error("Повреждённый черновик.");
            const source = store.get(this.key(ref.key));
            source.onsuccess = () => {
              if (typeof source.result !== "string") {
                error = new Error("Изображение черновика недоступно.");
                tx.abort();
                return;
              }
              asset.source = source.result;
              media.set(source.result, ref.key);
            };
          }
        } catch (e) {
          error = e;
          tx.abort();
        }
      };
      tx.oncomplete = () => {
        db.close();
        this.media = media;
        this.initialized = true;
        this.revision = revision;
        resolve(result);
      };
      tx.onabort = tx.onerror = () => {
        db.close();
        reject(
          error || tx.error || new Error("Не удалось прочитать черновик."),
        );
      };
    });
  }

  async write(record: RecoveryRecord): Promise<boolean> {
    if (!this.factory) return false;
    if (!this.initialized) await this.read();
    const next = new Map<string, string>();
    const head: MediaHead = {
      format: "creator-native-recovery-media",
      version: 1,
      revision: crypto.randomUUID(),
      record: copySceneValue(record),
      media: [],
    };
    head.record.projectData.document.assets.forEach((asset, index) => {
      if (!asset.source.startsWith("data:")) return;
      const source = asset.source;
      const key =
        next.get(source) ?? this.media.get(source) ?? crypto.randomUUID();
      next.set(source, key);
      head.media.push({ asset: index, key });
      asset.source = "";
    });
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.location.store, "readwrite"),
        store = tx.objectStore(this.location.store);
      let error: unknown;
      tx.oncomplete = () => {
        db.close();
        this.media = next;
        this.revision = head.revision;
        resolve(true);
      };
      tx.onabort = tx.onerror = () => {
        db.close();
        reject(
          error || tx.error || new Error("Не удалось сохранить черновик."),
        );
      };
      // Another tab may have replaced the draft and removed cached media.
      // Check the durable head inside the same write transaction.
      const previous = store.get(this.location.key);
      previous.onsuccess = () => {
        try {
          const old = previous.result as MediaHead | undefined;
          const current =
            old?.format === head.format && old.revision === this.revision;
          for (const [source, key] of next)
            if (!current || !this.media.has(source))
              store.put(source, this.key(key));
          store.put(head, this.location.key);
          const kept = new Set(next.values());
          if (old?.format === head.format)
            for (const { key } of old.media)
              if (!kept.has(key)) store.delete(this.key(key));
        } catch (e) {
          error = e;
          tx.abort();
        }
      };
    });
  }
}
